import React, { useState, useEffect } from 'react';
import { Calendar, User, Phone, MapPin, Globe, Upload, FileText, X, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Country list for nationality dropdown
const countries = [
  "Tanzania", "Kenya", "Uganda", "Rwanda", "Burundi", "South Africa", "Nigeria", "Ghana", "Ethiopia",
  "United States", "United Kingdom", "Canada", "Germany", "France", "Italy", "Spain", "Netherlands",
  "China", "India", "Japan", "South Korea", "Australia", "New Zealand", "Brazil", "Mexico",
  "United Arab Emirates", "Saudi Arabia", "Qatar", "Egypt", "Morocco", "Other"
];

const GuestBookingForm = ({ apartments = [], selectedApartment = null, onClose = null }) => {
  const [formData, setFormData] = useState({
    guest_name: '',
    email: '',
    phone: '',
    nationality: '',
    origin: '',
    check_in: '',
    check_out: '',
    apartment_id: selectedApartment?.id || '',
    apartment_name: selectedApartment?.name || '',
    notes: ''
  });
  const [idDocument, setIdDocument] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (selectedApartment) {
      setFormData(prev => ({
        ...prev,
        apartment_id: selectedApartment.id,
        apartment_name: selectedApartment.name
      }));
    }
  }, [selectedApartment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a JPG, PNG, WebP image or PDF document');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      setIdDocument(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setIdPreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setIdPreview(null);
      }
    }
  };

  const removeFile = () => {
    setIdDocument(null);
    setIdPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.guest_name || !formData.email || !formData.phone || 
        !formData.nationality || !formData.origin || !formData.check_in || !formData.check_out) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate dates
    const checkIn = new Date(formData.check_in);
    const checkOut = new Date(formData.check_out);
    if (checkOut <= checkIn) {
      toast.error('Check-out date must be after check-in date');
      return;
    }

    setSubmitting(true);

    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          submitData.append(key, formData[key]);
        }
      });
      
      if (idDocument) {
        submitData.append('id_document', idDocument);
      }

      const response = await fetch(`${BACKEND_URL}/api/bookings`, {
        method: 'POST',
        body: submitData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit booking');
      }

      setSubmitted(true);
      toast.success('Booking submitted successfully! We will contact you shortly.');
      
      // Reset form after delay
      setTimeout(() => {
        setFormData({
          guest_name: '',
          email: '',
          phone: '',
          nationality: '',
          origin: '',
          check_in: '',
          check_out: '',
          apartment_id: '',
          apartment_name: '',
          notes: ''
        });
        setIdDocument(null);
        setIdPreview(null);
        setSubmitted(false);
        if (onClose) onClose();
      }, 3000);

    } catch (error) {
      toast.error(error.message || 'Failed to submit booking');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-green-50 rounded-xl p-6 text-center">
        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
          <Check className="w-6 h-6 text-white" />
        </div>
        <h3 className="text-xl font-bold text-green-800 mb-1">Booking Submitted!</h3>
        <p className="text-green-600 text-sm">We will contact you shortly to confirm your reservation.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Guest Information */}
      <div className="space-y-3">
        <h3 className="text-base font-semibold text-amber-900 flex items-center gap-2">
          <User className="w-4 h-4" />
          Guest Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              name="guest_name"
              value={formData.guest_name}
              onChange={handleChange}
              placeholder="Enter your full name"
              required
              className="h-9 text-sm"
              data-testid="guest-name-input"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              required
              className="h-9 text-sm"
              data-testid="guest-email-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <Phone className="w-3 h-3 inline mr-1" />
              Phone <span className="text-red-500">*</span>
            </label>
            <Input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+255 XXX XXX XXX"
              required
              className="h-9 text-sm"
              data-testid="guest-phone-input"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <Globe className="w-3 h-3 inline mr-1" />
              Nationality <span className="text-red-500">*</span>
            </label>
            <select
              name="nationality"
              value={formData.nationality}
              onChange={handleChange}
              className="w-full h-9 px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
              required
              data-testid="guest-nationality-select"
            >
              <option value="">Select nationality</option>
              {countries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            <MapPin className="w-3 h-3 inline mr-1" />
            Coming from? <span className="text-red-500">*</span>
          </label>
          <Input
            type="text"
            name="origin"
            value={formData.origin}
            onChange={handleChange}
            placeholder="City, Country"
            required
            className="h-9 text-sm"
            data-testid="guest-origin-input"
          />
        </div>
      </div>

      {/* Stay Details */}
      <div className="space-y-3">
        <h3 className="text-base font-semibold text-amber-900 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Stay Details
        </h3>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Check-in <span className="text-red-500">*</span>
            </label>
            <Input
              type="date"
              name="check_in"
              value={formData.check_in}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              required
              className="h-9 text-sm"
              data-testid="check-in-date-input"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Check-out <span className="text-red-500">*</span>
            </label>
            <Input
              type="date"
              name="check_out"
              value={formData.check_out}
              onChange={handleChange}
              min={formData.check_in || new Date().toISOString().split('T')[0]}
              required
              className="h-9 text-sm"
              data-testid="check-out-date-input"
            />
          </div>
        </div>

        {apartments.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Preferred Apartment
            </label>
            <select
              name="apartment_id"
              value={formData.apartment_id}
              onChange={(e) => {
                const apt = apartments.find(a => a.id === e.target.value);
                setFormData(prev => ({
                  ...prev,
                  apartment_id: e.target.value,
                  apartment_name: apt?.name || ''
                }));
              }}
              className="w-full h-9 px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="apartment-select"
            >
              <option value="">Any available</option>
              {apartments.map(apt => (
                <option key={apt.id} value={apt.id}>{apt.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* ID Document Upload - Compact */}
      <div className="space-y-2">
        <h3 className="text-base font-semibold text-amber-900 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          ID Document
        </h3>
        
        {!idDocument ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-amber-500 transition-colors">
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileChange}
              className="hidden"
              id="id-upload"
              data-testid="id-document-input"
            />
            <label htmlFor="id-upload" className="cursor-pointer">
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-1" />
              <p className="text-gray-600 text-sm">Click to upload ID</p>
              <p className="text-xs text-gray-400">JPG, PNG, PDF (max 10MB)</p>
            </label>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg p-3 flex items-center justify-between bg-gray-50">
            <div className="flex items-center gap-2">
              {idPreview ? (
                <img src={idPreview} alt="ID Preview" className="w-12 h-12 object-cover rounded" />
              ) : (
                <div className="w-12 h-12 bg-amber-100 rounded flex items-center justify-center">
                  <FileText className="w-6 h-6 text-amber-600" />
                </div>
              )}
              <div>
                <p className="font-medium text-gray-800 text-sm truncate max-w-[150px]">{idDocument.name}</p>
                <p className="text-xs text-gray-500">{(idDocument.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            </div>
            <button
              type="button"
              onClick={removeFile}
              className="p-1.5 hover:bg-red-100 rounded-full transition-colors"
              data-testid="remove-id-btn"
            >
              <X className="w-4 h-4 text-red-500" />
            </button>
          </div>
        )}
      </div>

      {/* Additional Notes */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Additional Notes
        </label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={2}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
          placeholder="Any special requests..."
          data-testid="notes-textarea"
        />
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-amber-600 hover:bg-amber-700 text-white py-2.5"
        data-testid="submit-booking-btn"
      >
        {submitting ? (
          <span className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Submitting...
          </span>
        ) : (
          'Submit Booking Request'
        )}
      </Button>

      <p className="text-xs text-gray-500 text-center">
        By submitting, you agree to our terms. We will contact you to confirm.
      </p>
    </form>
  );
};

export default GuestBookingForm;
